// @owner ClientPlatform
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/label-has-associated-control */

import type {FC, MouseEventHandler, PropsWithChildren} from 'react';
import React, {useState} from 'react';
import {classNames} from '../../../helpers/classNameHelpers';
import {SigninPage, type SwitchToSigninPage} from '../../helpers/pageHelpers';
import {
  findBaseUrl,
  isSignupSupported,
  isSsoSupported,
  pathToRedirectTo,
  userDidSignIn,
} from '../../../helpers/signupHelpers';
import {
  StyledThirdPartySigninRow,
  StyledSeparator,
  StyledButtonIcon,
  StyledFieldsGroup,
} from '../../commonSigninStyles';
import {styled} from 'styled-components';
import {textStyleMixin} from '../../../common/components/text';
import {AuthCard} from '../../components/authCard/authCard';
import {Input} from '../../../common/components/input';
import {FullWidthButton} from '../../../common/components/button';
import {requester} from '../../../helpers/requestHelpers';
import googleIconSrc from '../../../assets/google.svg';
import officeIconSrc from '../../../assets/office.svg';
import ssoIconSrc from '../../../assets/sso.svg';

const StyledErrorMessage = styled.div`
  width: 100%;
  text-align: center;
  color: var(--color-text-danger);
  padding: 6px 5%;
  ${textStyleMixin({$variant: 'body-regular'})}
`;

const StyledLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  ${textStyleMixin({$variant: 'caption-regular'})};

  a {
    ${textStyleMixin({$variant: 'caption-semibold'})}
  }

  margin-top: 8px;
`;

interface LoginProps {
  switchToSigninPage: SwitchToSigninPage;
  googleSignin: MouseEventHandler<HTMLElement>;
  officeSignin: MouseEventHandler<HTMLElement>;
}

interface ForgotPasswordWindow extends Window {
  analytics?: {
    identify: (email: string) => void;
    track: (eventName: string) => void;
  };
}

function findLoginWindow() {
  return window as ForgotPasswordWindow;
}

export const Login: FC<PropsWithChildren<LoginProps>> = ({switchToSigninPage, googleSignin, officeSignin}) => {
  const [error, setError] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [twoFactorAuthToken, setTwoFactorAuthToken] = useState('');
  const [isSending, setIsSending] = useState(false);

  async function signin() {
    setIsSending(true);
    setError('');

    try {
      await requester.post('/api/1/signin', {
        email,
        password,
        two_factor_auth_token: twoFactorAuthToken,
      });

      const window = findLoginWindow();
      if (window.analytics) {
        window.analytics.identify(email);
        window.analytics.track('clicked_signin_app');
      }

      // Use the correct method depending on if the OSX app accept the base URL as param or not.
      userDidSignIn(pathToRedirectTo(), findBaseUrl());
    } catch (e) {
      setIsSending(false);
      setTwoFactorAuthToken('');

      const {response} = e;
      const {data} = response;

      if (response.status === 401) {
        setError(data.status);
        setErrorMessage(data.message);
        return;
      }

      // 404: email not found
      // 403: bad_password OR SSO is enabled
      // 429: rate_limit
      // 450: 2fa_auth_token_missing
      if ([404, 403, 429, 450].includes(response.status)) {
        const err = data.reason || data.status;

        // If company enforced OAuth login, switch to OAuth
        if (err === 'oauth_only') {
          switchToSigninPage(SigninPage.OAUTH_ONLY);
          return;
        }

        setError(err);
        return;
      }

      setError('unknown');
    }
  }

  return (
    <AuthCard
      name="signinForm"
      onSubmit={(e) => {
        e.preventDefault();
        signin();
      }}
    >
      <StyledFieldsGroup>
        <Input
          value={email}
          id="email-login"
          label="Email"
          type="email"
          name="email"
          placeholder="Email address"
          required
          autoFocus
          onChange={setEmail}
          error={error === 'not_found' ? 'Invalid email or password' : undefined}
        />
        <Input
          value={password}
          id="password-login"
          label="Password"
          type="password"
          name="password"
          placeholder="Password"
          required
          onChange={setPassword}
          error={findPasswordErrorText(error)}
        />
        {maybeRenderTwoFactorAuth(error, twoFactorAuthToken, setTwoFactorAuthToken)}
        {maybeRenderAuthRequired(error, errorMessage)}
        <FullWidthButton testId="signin" type="submit" disabled={isSending}>
          Sign in
        </FullWidthButton>
        <StyledLinks>
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              switchToSigninPage(SigninPage.FORGOT_PASSWORD);
            }}
          >
            Forgot password?
          </a>
          {maybeRenderGetStarted()}
        </StyledLinks>
      </StyledFieldsGroup>
      <StyledSeparator>or continue with</StyledSeparator>
      <StyledThirdPartySigninRow>
        <FullWidthButton
          type="button"
          variant="secondary"
          onClick={googleSignin}
          leadingAccessory={<StyledButtonIcon src={googleIconSrc} alt="" />}
        >
          Google
        </FullWidthButton>
        <FullWidthButton
          type="button"
          variant="secondary"
          onClick={officeSignin}
          leadingAccessory={<StyledButtonIcon src={officeIconSrc} alt="" />}
        >
          Office 365
        </FullWidthButton>
        {maybeRenderSsoSignin(switchToSigninPage)}
      </StyledThirdPartySigninRow>
    </AuthCard>
  );
};

function findPasswordErrorText(error: string) {
  switch (error) {
    case 'bad_password':
      return 'Invalid email or password';
    case 'rate_limit':
      return 'Too many attempts, wait a little';
    case 'sso_enabled':
      return 'Your company has enabled SSO';
    case 'unknown':
      return 'Unknown error';
    default:
      return undefined;
  }
}

function maybeRenderTwoFactorAuth(
  error: string,
  twoFactorAuthToken: string,
  setTwoFactorAuthToken: (value: string) => void
) {
  if (error !== 'missing_two_factor_auth' && error !== 'invalid_two_factor_auth') {
    return null;
  }

  return (
    <Input
      id="token-login"
      type="text"
      label="Two factor code"
      name="token"
      placeholder="Two factor code"
      required
      autoFocus
      value={twoFactorAuthToken || ''}
      onChange={setTwoFactorAuthToken}
      error={error === 'invalid_two_factor_auth' ? 'This code is invalid' : undefined}
    />
  );
}

function maybeRenderAuthRequired(error: string, errorMessage: string) {
  if (error !== 'authentication_required') {
    return null;
  }

  return (
    <StyledErrorMessage
      className={classNames({
        'error-message': true,
        'text-small': true,
        visible: error === 'authentication_required',
      })}
    >
      {errorMessage}
    </StyledErrorMessage>
  );
}

function maybeRenderGetStarted() {
  if (!isSignupSupported()) {
    return null;
  }

  return (
    <div>
      Don&#39;t have an account?{' '}
      <a href="https://front.com/signup" target="_blank" rel="noreferrer">
        Try for Free
      </a>
    </div>
  );
}

function maybeRenderSsoSignin(switchToSigninPage: SwitchToSigninPage) {
  if (!isSsoSupported()) {
    return null;
  }

  return (
    <FullWidthButton
      type="button"
      variant="secondary"
      id="sso-signin-link"
      testId="sso-signin"
      onClick={() => switchToSigninPage(SigninPage.INIT_SIGNIN)}
      leadingAccessory={<StyledButtonIcon src={ssoIconSrc} alt="" />}
    >
      SAML SSO
    </FullWidthButton>
  );
}
